<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { daysSince } from '../lib/format';
  import { currentView, stages, stageColor, stageLabel, terms, avatarColor, initialsFor, clientContactSeed, agentName, STAGE_ORDER, userEmail, refreshStages, setClientStage } from '../lib/stores';
  import { readCache, writeCache } from '../lib/swr';
  import { contactsCacheKey, invalidateContactsCache } from '../lib/contactCache';
  import {
    getContacts, createContact, updateContact, deleteContact,
    bulkCreateContacts, deleteAllProspectContacts,
    exportContactsUrl, setStage,
    promoteClient,
    type Contact,
  } from '../lib/api';

  const CONTACTS_CACHE = contactsCacheKey($userEmail);   // full list (no active search)
  // Hydrate synchronously at init so the first render already has the list —
  // no loading flash when navigating to the page.
  const _contactsInit = readCache<Contact[]>(CONTACTS_CACHE);

  let contacts = $state<Contact[]>(_contactsInit ?? []);
  let search = $state('');
  let stageFilter = $state<string[]>([]);
  let loading = $state(_contactsInit ? false : true);
  let contactsStale = $state(false);
  let addOpen = $state(false);
  let editingEmail = $state('');
  let toast = $state('');
  let toastError = $state(false);
  let expandedClients = $state<Set<string>>(new Set());
  let expandedStages = $state<Set<string>>(new Set());

  let openDD = $state<'stage' | 'more' | null>(null);

  function toggleStageFilter(s: string) {
    if (stageFilter.includes(s)) {
      stageFilter = stageFilter.filter(x => x !== s);
    } else {
      stageFilter = [...stageFilter, s];
    }
  }

  // Keyed on `.fchip-wrap` so a click on the chip itself (which is inside it) doesn't
  // immediately re-close the menu it just opened.
  function onWindowClick(e: MouseEvent) {
    const t = e.target as HTMLElement;
    if (openDD && !t.closest('.fchip-wrap')) openDD = null;
    if (stageMenu && !t.closest('.stage-pill, .stage-menu')) stageMenu = null;
  }

  /* The stage menu is `position: fixed` and rendered outside the table deliberately.
     `.data-table` is `overflow: hidden` (it clips the header to the rounded corners), which
     would cut off any absolutely-positioned menu opened from inside a row. A fixed element's
     containing block is the viewport, so it escapes that clip — the cost is that it no longer
     travels with its row, hence the scroll/resize teardown below.
     🔴 **It must stay OUTSIDE `.data-table-wrap`, and that is now a second, harder rule.**
     That wrapper carries `container-type: inline-size` (for the `@container` column rules), and
     a container is a **containing block for `position: fixed` descendants** — so a fixed menu
     moved inside it would silently start positioning against the wrapper instead of the
     viewport, putting it in the wrong place with nothing in the CSS to explain why.
     Dashboard's `.dash-modal-bg` is outside its wrapper for the same reason. */
  let stageMenu = $state<{ client: string; top: number; right: number } | null>(null);

  const stageOf = (cname: string) => $stages[cname] || '— unset —';

  function toggleStageMenu(e: MouseEvent, cname: string) {
    e.stopPropagation();                       // the row itself toggles expand on click
    if (stageMenu?.client === cname) { stageMenu = null; return; }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Right-anchored: the pill sits at the row's right edge, so a left-anchored menu would
    // run past `.main-content`, which is `overflow-x: clip`.
    stageMenu = {
      client: cname,
      top: r.bottom + 6,
      right: document.documentElement.clientWidth - r.right,
    };
  }

  $effect(() => {
    if (!stageMenu) return;
    const close = () => stageMenu = null;
    // Capture: the page scrolls inside `.main`, not on `window`, so a bubbling listener
    // never fires and the menu would hang in place over unrelated rows.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  });

  const hasActiveFilters = $derived(stageFilter.length > 0 || search.length > 0);
  const stageLbl = $derived(
    stageFilter.length === 0 ? 'All'
      : stageFilter.length === 1 ? stageFilter[0]
      : stageFilter.length + ' selected'
  );

  // The number beside each stage is **the one its group header shows** — clients for a
  // normal stage, contacts for Non-Clients (which is a single pseudo-client). Deliberately
  // NOT filtered by `stageFilter`: a stage you have not selected must still show its real
  // size, or every unselected row reads 0.
  const stageCounts = $derived.by(() => {
    const byClient = groupByClient(contacts);
    const out: Record<string, number> = {};
    for (const cname of Object.keys(byClient)) {
      const st = cname === 'Non-Clients' ? 'Non-Clients' : ($stages[cname] || '— unset —');
      out[st] = (out[st] || 0) + (st === 'Non-Clients' ? byClient[cname].length : 1);
    }
    return out;
  });

  // `contacts` is already narrowed by the search (a server query); `stageFilter` is applied
  // client-side. So the total is what the search returned and the shown count is what survives
  // the stage filter — which is exactly what the table renders.
  const shownCount = $derived.by(() => {
    if (!stageFilter.length) return contacts.length;
    const byClient = groupByClient(contacts);
    let n = 0;
    for (const [cname, list] of Object.entries(byClient)) {
      const st = cname === 'Non-Clients' ? 'Non-Clients' : ($stages[cname] || '— unset —');
      if (stageFilter.includes(st)) n += list.length;
    }
    return n;
  });

  async function clearFilters() {
    stageFilter = [];
    openDD = null;
    if (search) { search = ''; await refresh(); }   // search is a server query, so re-fetch
  }
  let ncTipOpen = $state(false);
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Add form
  let newEmail = $state('');
  let newName = $state('');
  let newClient = $state('');
  let newTitle = $state('');
  let newPhone = $state('');
  let newNotes = $state('');

  // Edit form
  let editName = $state('');
  let editTitle = $state('');
  let editPhone = $state('');
  let editLinkedin = $state('');
  let editNotes = $state('');

  // Promote dialog (for domain groups not yet a client)
  let promoteDialog = $state<{ domain: string; suggestedName: string } | null>(null);
  let promoteName = $state('');

  // Confirm Non-Clients stage change
  let confirmNonClient = $state('');

  /* This list is the page's whole stage vocabulary: the filter chips, the per-client
     per-row stage menu, AND the render loop — a stage missing here means its clients are
     grouped and then never drawn. Order comes from STAGE_ORDER, shared with Dashboard so
     the two cannot drift apart by hand. `Non-Clients` is appended because this page is the
     default place parked clients stay visible — Dashboard requires its Non-Clients filter. An entry
     with no key in `STAGE_COLORS` still renders, but with the grey fallback dot. */
  const allStages = [...STAGE_ORDER, 'Non-Clients'];
  const AUTO_COLLAPSE_THRESHOLD = 100;

  let expansionInitialized = false;

  const PERSONAL_DOMAINS = new Set([
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.ca', 'yahoo.co.uk', 'hotmail.com',
    'outlook.com', 'live.com', 'msn.com', 'aol.com', 'icloud.com', 'me.com',
    'mail.com', 'protonmail.com', 'proton.me', 'zoho.com', 'yandex.com',
    'gmx.com', 'gmx.net', 'fastmail.com', 'tutanota.com',
    'bigpond.com', 'bigpond.net.au',
  ]);

  onMount(async () => {
    // App loaded the shared stage map from the server before mounting this page.
    await refresh();
    if (!expansionInitialized) {
      const counts: Record<string, number> = {};
      for (const c of contacts) {
        const s = $stages[c.client] || '— unset —';
        counts[s] = (counts[s] || 0) + 1;
      }
      expandedStages = new Set(
        [...allStages, '— unset —'].filter(s =>
          s !== 'Non-Clients' && (counts[s] ?? 0) <= AUTO_COLLAPSE_THRESHOLD
        )
      );
      expansionInitialized = true;
    }
  });

  // Search is a server query behind a 400ms debounce, so two refreshes can be in flight at
  // once and the SLOWER one can resolve last. Only the newest may write — otherwise typing
  // fast paints a stale list over a newer one. (Same class as the client-switch race, (dd).)
  let refreshSeq = 0;

  function invalidateAfterWrite() {
    ++refreshSeq; // a read begun before this write cannot restore old contacts
    invalidateContactsCache($userEmail);
    contactsStale = true;
  }

  async function refresh() {
    const seq = ++refreshSeq;
    closeStageMenu();
    // Only blank on a true cold start (no cache yet). Otherwise keep showing
    // the current/cached list and swap it in place when the fetch resolves —
    // no loading flash, smooth transition.
    if (!contacts.length) loading = true;
    try {
      const [c] = await Promise.all([getContacts(search), refreshStages()]);
      if (seq !== refreshSeq) return;          // superseded mid-flight — drop this result
      contacts = c;
      contactsStale = false;
      // Cache only the full (unfiltered) list — search results are transient.
      if (!search.trim()) writeCache(CONTACTS_CACHE, c);
    } catch (err: any) {
      if (seq === refreshSeq) {
        contactsStale = true;
        showError(err?.message || 'Could not load contacts');
      }
    } finally {
      // `finally`, not the try's tail — a rejected fetch on a cold start otherwise
      // leaves this true and the page spins forever. Only the newest clears it, or a
      // stale response hides the spinner while the current request is still running.
      if (seq === refreshSeq) loading = false;
    }
  }

  function handleSearchInput() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => refresh(), 400);
  }

  function handleSearchKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      if (debounceTimer) clearTimeout(debounceTimer);
      refresh();
    }
  }

  async function handleAdd() {
    if (!newEmail || !newEmail.includes('@')) { showToast('Enter a valid email'); return; }
    if (!newClient.trim()) { showToast(`${$terms.counterparty_title} name is required`); return; }
    try {
      await createContact({ email: newEmail, name: newName, client: newClient, title: newTitle, phone: newPhone, notes: newNotes });
    } catch (err: any) {
      // Return before clearing: the form keeps what they typed so they can retry.
      showError(err?.message || 'Could not add the contact');
      return;
    }
    invalidateAfterWrite();
    addOpen = false;
    newEmail = newName = newClient = newTitle = newPhone = newNotes = '';
    showToast('Contact added');
    await refresh();
  }

  function startEdit(c: Contact) {
    editingEmail = c.email;
    editName = c.name;
    editTitle = c.title;
    editPhone = c.phone;
    editLinkedin = c.linkedin;
    editNotes = c.notes;
  }

  async function handleSaveEdit(c: Contact) {
    try {
      await updateContact(c.email, { ...c, name: editName, title: editTitle, phone: editPhone, linkedin: editLinkedin, notes: editNotes });
    } catch (err: any) {
      // The editor stays open on failure — closing it would discard their edit.
      showError(err?.message || 'Could not save the contact');
      return;
    }
    invalidateAfterWrite();
    contacts = contacts.map(row => row.email === c.email
      ? { ...row, name: editName, title: editTitle, phone: editPhone, linkedin: editLinkedin, notes: editNotes }
      : row);
    editingEmail = '';
    showToast('Contact updated');
    await refresh();
  }

  // Deleting is permanent, and when it removes a client's last contact the backend
  // drops that client's stage with it (`routers/contacts.py:432`) — so it asks first,
  // like every other destructive action in the app.
  async function handleDelete(c: Contact) {
    const who = c.name ? `${c.name} (${c.email})` : c.email;
    if (!confirm(`Delete ${who}?\n\nThis cannot be undone.`)) return;
    try {
      await deleteContact(c.email);
    } catch (err: any) {
      showError(err?.message || 'Could not remove the contact');
      return;
    }
    invalidateAfterWrite();
    contacts = contacts.filter(row => row.email !== c.email);
    showToast('Contact removed');
    await refresh();
  }

  // Non-success messages render in the red toast. Detected HERE rather than at
  // the ~30 call sites in this file: each already states its outcome in words,
  // and a missed flag fails silently back to green — which is the bug this
  // fixes. Pass `true` explicitly to override.
  const TOAST_FAILURE = /\b(fail|failed|error|invalid|required|can'?t|cannot|empty|denied|unable)\b|^(pick|set|enter|select) /i;
  function showToast(msg: string, isError = TOAST_FAILURE.test(msg)) {
    toast = msg;
    toastError = isError;
    setTimeout(() => toast = '', isError ? 5000 : 3000);
  }

  // Anything reaching a `catch` IS a failure — say so explicitly rather than hoping the
  // wording matches TOAST_FAILURE. The trap is `err?.message || 'X failed'`: the FALLBACK
  // carries the safe word, but when the server does send a message ("Draft not found",
  // "5 emails are already pinned") that fallback never runs, and the toast came out green.
  const showError = (msg: string) => showToast(msg, true);

  async function handleStageChange(client: string, newStage: string) {
    const oldStage = $stages[client] || '— unset —';
    // Confirm before moving to Non-Clients
    if (newStage === 'Non-Clients' && oldStage !== 'Non-Clients') {
      confirmNonClient = client;
      return;
    }
    await doStageChange(client, newStage);
  }

  async function doStageChange(client: string, newStage: string) {
    confirmNonClient = '';
    try {
      await setStage(client, newStage);
      setClientStage(client, newStage);
    } catch (err: any) {
      // The pill still shows the stage they picked; the refresh puts it back.
      showError(err?.message || 'Could not change the stage');
      await refresh();
      return;
    }
    showToast(`${client.replace(/_/g, ' ')} → ${$stageLabel(newStage)}`);
    await refresh();
  }

  function normalizeClientName(name: string): string {
    return name.trim().replace(/ /g, '_');
  }

  function groupByClient(contacts: Contact[]): Record<string, Contact[]> {
    const groups: Record<string, Contact[]> = {};
    for (const c of contacts) {
      const key = c.client ? normalizeClientName(c.client) : '— Unknown —';
      (groups[key] ??= []).push(c);
    }
    return groups;
  }

  function groupByStage(clientGroups: Record<string, Contact[]>): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    const activeFilter = stageFilter.length > 0;
    for (const cname of Object.keys(clientGroups).sort()) {
      const s = cname === 'Non-Clients' ? 'Non-Clients' : ($stages[cname] || '— unset —');
      if (activeFilter && !stageFilter.includes(s)) continue;
      (result[s] ??= []).push(cname);
    }
    return result;
  }

  function groupNonClientsByDomain(ncContacts: Contact[]): {
    orgGroups: Record<string, Contact[]>;
    personalContacts: Contact[];
  } {
    const orgGroups: Record<string, Contact[]> = {};
    const personalContacts: Contact[] = [];
    for (const c of ncContacts) {
      const domain = c.email.split('@')[1]?.toLowerCase() || '';
      if (PERSONAL_DOMAINS.has(domain)) {
        personalContacts.push(c);
      } else {
        (orgGroups[domain] ??= []).push(c);
      }
    }
    return { orgGroups, personalContacts };
  }

  function orgDisplayName(domain: string, contacts: Contact[]): string {
    return contacts[0]?.org || domainToName(domain);
  }

  function domainToName(domain: string): string {
    const base = domain.split('.')[0];
    return base
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  function openPromoteDialog(domain: string, contacts: Contact[] = []) {
    // For personal emails (full email passed as domain), use contact name as suggested org
    const isPersonalEmail = domain.includes('@');
    const orgName = contacts[0]?.org || (isPersonalEmail ? (contacts[0]?.name || domain.split('@')[0]) : domainToName(domain));
    promoteDialog = { domain, suggestedName: orgName };
    promoteName = orgName;
  }

  async function handlePromote() {
    if (!promoteDialog || !promoteName.trim()) return;
    loading = true;
    try {
      const r = await promoteClient(promoteDialog.domain, promoteName.trim());
      invalidateAfterWrite();
      showToast(`Created client: ${r.client}`);
      promoteDialog = null;
      promoteName = '';
      await refresh();
    } catch (e: any) {
      showError('Failed: ' + e.message);
      loading = false;
    }
  }

  function daysAgo(dateStr: string): number {
    if (!dateStr || dateStr.length !== 10) return 999;
    return daysSince(dateStr);
  }

  /* `stageMenu` is `position: fixed` at coordinates measured when it opened, so anything that
     moves the rows underneath leaves it pointing at the wrong one. Scroll and resize are handled
     by listeners; these are the in-app movers — expanding/collapsing a group, and `refresh()`
     re-rendering the table after a stage change. */
  function closeStageMenu() { stageMenu = null; }

  function toggleStageExpand(s: string) {
    closeStageMenu();
    const next = new Set(expandedStages);
    if (next.has(s)) next.delete(s); else next.add(s);
    expandedStages = next;
  }

  // ── Chunked rendering: fast initial render, explicit Load More ──
  const INITIAL_CHUNK = 1000;
  const CHUNK_INCREMENT = 1000;
  let renderLimits = $state<Record<string, number>>({});
  function loadMore(stage: string) {
    renderLimits = { ...renderLimits, [stage]: (renderLimits[stage] ?? INITIAL_CHUNK) + CHUNK_INCREMENT };
  }

  function toggleClientExpand(c: string) {
    closeStageMenu();
    const next = new Set(expandedClients);
    if (next.has(c)) next.delete(c); else next.add(c);
    expandedClients = next;
  }

  // ── CSV import ────────────────────────────────────────────────────────────
  type FieldKey = 'ignore' | 'email' | 'first_name' | 'last_name' | 'name' | 'client' | 'title' | 'phone' | 'linkedin' | 'notes' | 'org' | 'tag';
  const FIELD_OPTIONS: { value: FieldKey; label: string; required?: boolean }[] = [
    { value: 'ignore',     label: 'Ignore' },
    { value: 'email',      label: 'Email',      required: true },
    { value: 'name',       label: 'Full name' },
    { value: 'first_name', label: 'First name' },
    { value: 'last_name',  label: 'Last name' },
    // `value` is the CSV mapping key the importer sends; only `label` is shown.
    { value: 'client',     label: get(terms).counterparty_title ?? 'Client', required: true },
    { value: 'title',      label: 'Title' },
    { value: 'phone',      label: 'Phone' },
    { value: 'linkedin',   label: 'LinkedIn' },
    { value: 'org',        label: 'Org / company display' },
    { value: 'notes',      label: 'Notes' },
    { value: 'tag',        label: 'Tag (Strafe filter)' },
  ];

  let importDefaultTag = $state('');  // bulk-applied when no tag column is mapped or row's tag is empty

  let importOpen = $state(false);
  let importFileName = $state('');
  let importHasHeader = $state(true);
  let importColumns = $state<string[]>([]);    // header row, or 'Column 1' / 'Column 2' / ...
  let importRows = $state<string[][]>([]);     // raw data rows (excluding header row when present)
  let importMapping = $state<FieldKey[]>([]);  // index into importColumns
  let importing = $state(false);
  let importDone = $state(false);
  let importAutoCloseHandle: ReturnType<typeof setTimeout> | null = null;
  let importProgress = $state({ done: 0, total: 0, imported: 0, skipped: 0, errors: 0, errorRows: [] as { row: number; reason: string }[] });

  function parseCsv(text: string): string[][] {
    // Strip BOM
    if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
    const rows: string[][] = [];
    let i = 0, field = '', row: string[] = [], inQuotes = false;
    while (i < text.length) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
          inQuotes = false; i++; continue;
        }
        field += ch; i++; continue;
      }
      if (ch === '"') { inQuotes = true; i++; continue; }
      if (ch === ',') { row.push(field); field = ''; i++; continue; }
      if (ch === '\r') { i++; continue; }
      if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
      field += ch; i++;
    }
    // Tail
    if (field.length || row.length) { row.push(field); rows.push(row); }
    // Drop trailing empty rows
    while (rows.length && rows[rows.length - 1].every(c => !c)) rows.pop();
    return rows;
  }

  function autoDetectMapping(headers: string[]): FieldKey[] {
    const norm = (s: string) => s.toLowerCase().trim().replace(/[\s_\-.]+/g, '');
    return headers.map(h => {
      const n = norm(h);
      if (['email', 'emailaddress', 'mail', 'workemail', 'primaryemail'].includes(n)) return 'email';
      if (['firstname', 'first', 'givenname', 'given'].includes(n)) return 'first_name';
      if (['lastname', 'last', 'surname', 'familyname', 'family'].includes(n)) return 'last_name';
      if (['name', 'fullname', 'contact', 'contactname'].includes(n)) return 'name';
      if (['client', 'company', 'companyname', 'organization', 'organisation'].includes(n)) return 'client';
      if (['org', 'orgname'].includes(n)) return 'org';
      if (['title', 'role', 'position', 'jobtitle', 'job'].includes(n)) return 'title';
      if (['phone', 'mobile', 'cell', 'tel', 'telephone', 'phonenumber'].includes(n)) return 'phone';
      if (['linkedin', 'linkedinurl', 'linkedinprofile', 'li', 'liurl'].includes(n)) return 'linkedin';
      if (['notes', 'note', 'comment', 'comments', 'bio', 'founderbio'].includes(n)) return 'notes';
      if (['tag', 'tags', 'category', 'segment', 'list', 'group'].includes(n)) return 'tag';
      return 'ignore';
    });
  }

  async function handleImportFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    importFileName = file.name;
    const text = await file.text();
    const parsed = parseCsv(text);
    input.value = ''; // allow re-selecting the same file later
    if (!parsed.length) { showToast('CSV is empty'); return; }
    rebuildImportPreview(parsed, true);
  }

  // Recompute columns/rows/mapping based on whether the first row is a header
  let importParsed = $state<string[][]>([]);
  function rebuildImportPreview(parsed: string[][], reset: boolean) {
    importParsed = parsed;
    const colCount = parsed.reduce((m, r) => Math.max(m, r.length), 0);
    if (importHasHeader && parsed.length > 0) {
      const headers = parsed[0].slice(0, colCount).map((h, i) => h.trim() || `Column ${i + 1}`);
      importColumns = headers;
      importRows = parsed.slice(1).map(r => normalizeRow(r, colCount));
      if (reset) importMapping = autoDetectMapping(headers);
    } else {
      importColumns = Array.from({ length: colCount }, (_, i) => `Column ${i + 1}`);
      importRows = parsed.map(r => normalizeRow(r, colCount));
      if (reset) importMapping = importColumns.map(() => 'ignore' as FieldKey);
    }
  }

  function normalizeRow(r: string[], colCount: number): string[] {
    const out = r.slice(0, colCount);
    while (out.length < colCount) out.push('');
    return out;
  }

  function toggleHeader() {
    importHasHeader = !importHasHeader;
    if (importParsed.length) rebuildImportPreview(importParsed, true);
  }

  function setMapping(idx: number, value: FieldKey) {
    const next = [...importMapping];
    // Enforce uniqueness for fields that should appear once (everything except 'ignore', 'first_name', 'last_name' could in theory repeat — but we want one column per role)
    if (value !== 'ignore') {
      for (let i = 0; i < next.length; i++) {
        if (i !== idx && next[i] === value) next[i] = 'ignore';
      }
    }
    next[idx] = value;
    importMapping = next;
  }

  // Derive a client/company name from an email's domain, mirroring the
  // auto-CRM logic in gmail_poller.py: take the domain stem (everything
  // before the first `.`), replace non-word chars with `_`, strip surrounding
  // `_`, and title-case. So `andy@r2pi.co` → "R2Pi", `pablo@abadcapital.com`
  // → "Abadcapital", `kumar@engineersf.com` → "Engineersf".
  function clientNameFromEmailDomain(email: string): string {
    const domain = (email.split('@')[1] || '').toLowerCase();
    if (!domain) return '';
    const stem = domain.split('.')[0];
    if (!stem) return '';
    const cleaned = stem.replace(/[^\w]/g, '_').replace(/^_+|_+$/g, '');
    if (!cleaned) return '';
    // Python str.title() — capitalize first letter of each contiguous run of
    // alphabetic chars (digits act as word boundaries, so "r2pi" → "R2Pi").
    return cleaned.replace(/[a-z]+/gi, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
  }

  function buildContactFromRow(row: string[]): Partial<Contact> | null {
    const data: Record<string, string> = {};
    for (let i = 0; i < importMapping.length; i++) {
      const m = importMapping[i];
      if (m === 'ignore') continue;
      const v = (row[i] || '').trim();
      if (!v) continue;
      data[m] = v;
    }
    // Combine first_name + last_name if name not provided
    if (!data.name && (data.first_name || data.last_name)) {
      data.name = [data.first_name, data.last_name].filter(Boolean).join(' ').trim();
    }
    delete data.first_name;
    delete data.last_name;
    // Apply bulk-tag fallback when the row didn't supply one
    if (!data.tag && importDefaultTag.trim()) data.tag = importDefaultTag.trim();
    if (!data.email || !data.email.includes('@')) return null;
    // Fall back to the email-domain stem when client/company is missing —
    // matches the same derivation gmail_poller does for auto-discovered
    // clients, so manually-imported rows merge cleanly with auto-CRM rows.
    if (!data.client) data.client = clientNameFromEmailDomain(data.email);
    if (!data.client) return null;
    return data as Partial<Contact>;
  }

  const importHasEmail = $derived(importMapping.includes('email'));
  const importHasClient = $derived(importMapping.includes('client'));
  const importHasTag = $derived(importMapping.includes('tag'));
  const importPreviewRows = $derived(importRows.slice(0, 3).map(r => buildContactFromRow(r)));

  function closeImport() {
    if (importing) return;
    if (importAutoCloseHandle) { clearTimeout(importAutoCloseHandle); importAutoCloseHandle = null; }
    importOpen = false;
    importFileName = '';
    importColumns = [];
    importRows = [];
    importMapping = [];
    importParsed = [];
    importDone = false;
    importProgress = { done: 0, total: 0, imported: 0, skipped: 0, errors: 0, errorRows: [] };
  }

  async function runImport() {
    importing = true;
    importDone = false;

    // Phase 1: pre-build every contact client-side (instant, no network).
    // Skipped rows (bad email or no client even after domain fallback) are
    // counted here. They're NOT included in the progress bar's denominator —
    // the bar tracks only the actual import work in phase 2.
    const valid: Partial<Contact>[] = [];
    let skipped = 0;
    for (let i = 0; i < importRows.length; i++) {
      const contact = buildContactFromRow(importRows[i]);
      if (!contact) { skipped++; continue; }
      valid.push(contact);
    }

    // Phase 2: ship in batches of 500. Progress bar = imported / valid.length,
    // updated after every batch so the bar advances during the actual upload.
    importProgress = {
      done: 0,
      total: valid.length,
      imported: 0,
      skipped,
      errors: 0,
      errorRows: [],
    };

    const CHUNK = 500;
    let imported = 0;
    let done = 0;
    for (let i = 0; i < valid.length; i += CHUNK) {
      const batch = valid.slice(i, i + CHUNK);
      try {
        const res = await bulkCreateContacts(batch, importDefaultTag.trim());
        invalidateAfterWrite();
        imported += (res.added || 0) + (res.updated || 0);
      } catch (err: any) {
        const reason = err?.message || String(err);
        importProgress = {
          ...importProgress,
          errors: importProgress.errors + batch.length,
          errorRows: [...importProgress.errorRows, { row: i + 1, reason }].slice(0, 20),
        };
      }
      done += batch.length;
      importProgress = { ...importProgress, done, imported };
    }

    importing = false;
    importDone = true;
    await refresh();
    showToast(`Imported ${imported} contact${imported === 1 ? '' : 's'}`);
    if (importProgress.errors === 0 && importProgress.skipped === 0) {
      importAutoCloseHandle = setTimeout(() => { closeImport(); }, 1400);
    }
  }

  async function removeAllProspects() {
    const ok = confirm(
      `Remove ALL contacts at stage '${$stageLabel('Prospect')}'?\n\n` +
      "This is intended as a wipe-and-restart after a botched bulk CSV import.\n" +
      `Contacts at any other stage (${['Engaged', 'Active Deal', 'Near Close', 'Client']
        .map(s => $stageLabel(s)).join(', ')}, etc.)\n` +
      `are NOT touched, so ${$terms.engagement_title.toLowerCase()}s already in flight stay safe.\n\n` +
      "This cannot be undone."
    );
    if (!ok) return;
    try {
      const r = await deleteAllProspectContacts();
      invalidateAfterWrite();
      await refresh();
      showToast(
        `Removed ${r.deleted_contacts} contact${r.deleted_contacts === 1 ? '' : 's'} ` +
        `across ${r.prospect_clients_total} prospect client${r.prospect_clients_total === 1 ? '' : 's'}` +
        (r.deleted_clients ? ` · ${r.deleted_clients} empty client${r.deleted_clients === 1 ? '' : 's'} also cleaned up` : '')
      );
    } catch (err: any) {
      showError('Failed to remove prospects: ' + (err?.message || ''));
    }
  }
</script>

<div class="page-title">Contacts</div>
{#if contactsStale}
  <div class="text-dim" role="status" style="margin:8px 0; font-size:13px;">
    Contact list may be out of date. <button type="button" onclick={refresh}>Retry</button>
  </div>
{/if}

<!-- Toolbar — search takes the row's slack, the Stage chip holds its width at the end. One
     chip per filter axis is what keeps this one line however many stages exist. -->
<svelte:window onclick={onWindowClick} />

<div class="page-toolbar">
  <input class="page-toolbar-search" bind:value={search} placeholder="Search name, email, company, title..."
    aria-label="Search contacts" oninput={handleSearchInput} onkeydown={handleSearchKeydown} />
  <div class="fchip-wrap">
    <button class="fchip" class:set={stageFilter.length > 0}
      aria-haspopup="menu" aria-expanded={openDD === 'stage'}
      onclick={() => openDD = openDD === 'stage' ? null : 'stage'}>
      Stage <span class="fchip-v">{stageLbl}</span> <span class="fchip-ar">▾</span>
    </button>
    {#if openDD === 'stage'}
      <div class="fchip-dd" role="menu">
        {#each allStages as s}
          {@const n = stageCounts[s] ?? 0}
          <button class="fchip-opt" class:on={stageFilter.includes(s)} class:empty={n === 0}
            role="menuitemcheckbox" aria-checked={stageFilter.includes(s)}
            onclick={() => toggleStageFilter(s)}>
            <span class="fchip-check">✓</span>
            <span class="stage-dot" style="background:{stageColor(s)};"></span>
            {$stageLabel(s)}<span class="fchip-count">{n}</span>
          </button>
        {/each}
      </div>
    {/if}
  </div>
  {#if hasActiveFilters}
    <button class="fchip-clear" onclick={clearFilters}>Clear ✕</button>
  {/if}
  <span class="count-label">
    {#if stageFilter.length}<b>{shownCount}</b> of {contacts.length} contacts
    {:else}<b>{contacts.length}</b> contact{contacts.length === 1 ? '' : 's'}{/if}
  </span>
  <div class="page-toolbar-spacer"></div>
  <!-- ONE flex item, not two. Left separate they wrap onto separate lines and each lands hard
       against the LEFT edge — and `.fchip-dd` opens leftward, so a chip sitting at the left of
       a line has nowhere to open into and the menu runs off-screen. -->
  <div class="page-toolbar-actions">
    <button class="action-btn primary-action" onclick={() => addOpen = true}>+ Add contact</button>
    <!-- Import / Export / Remove-all sit behind this rather than on the bar. Remove-all wipes
         every Prospect-stage contact and must not read as a peer of Export. -->
    <div class="fchip-wrap">
    <button class="fchip" aria-label="More actions" aria-haspopup="menu"
      aria-expanded={openDD === 'more'}
      onclick={() => openDD = openDD === 'more' ? null : 'more'}>⋯</button>
    {#if openDD === 'more'}
      <div class="fchip-dd" role="menu">
        <button class="fchip-opt" role="menuitem"
          onclick={() => { openDD = null; importOpen = true; }}>Import CSV</button>
        <a class="fchip-opt" role="menuitem" href={exportContactsUrl()} download
          onclick={() => openDD = null}>Export CSV</a>
        <button class="fchip-opt danger" role="menuitem"
          title={`Wipe all contacts whose ${$terms.counterparty} is at stage '${$stageLabel('Prospect')}'. Use after a botched bulk import.`}
          onclick={() => { openDD = null; removeAllProspects(); }}>Remove all {$terms.prospect_plural}</button>
      </div>
    {/if}
    </div>
  </div>
</div>

{#if importOpen}
  <div class="import-overlay" role="dialog" aria-modal="true" aria-labelledby="import-title">
    <div class="import-modal card">
      <div class="import-header">
        <div class="font-bold" id="import-title">Import contacts from CSV</div>
        <button class="import-close" onclick={closeImport} disabled={importing} aria-label="Close">×</button>
      </div>

      {#if importDone}
        {@const ok = importProgress.errors === 0 && importProgress.skipped === 0}
        {@const partial = !ok && importProgress.imported > 0}
        <div class="import-result">
          <div class="import-result-icon" class:ok class:warn={!ok && partial} class:err={!ok && !partial}>
            {#if ok}✓{:else if partial}!{:else}×{/if}
          </div>
          <div class="import-result-title">
            {#if ok}
              Imported {importProgress.imported} contact{importProgress.imported === 1 ? '' : 's'}
            {:else if partial}
              Imported {importProgress.imported} of {importProgress.total} — {importProgress.errors + importProgress.skipped} not imported
            {:else}
              Import failed — 0 of {importProgress.total} contacts saved
            {/if}
          </div>
          <div class="import-result-stats">
            <div class="stat">
              <div class="stat-num" style="color: var(--green);">{importProgress.imported}</div>
              <div class="stat-label">imported</div>
            </div>
            <div class="stat">
              <div class="stat-num" style="color: var(--text-muted);">{importProgress.skipped}</div>
              <div class="stat-label">skipped</div>
            </div>
            <div class="stat">
              <div class="stat-num" style="color: {importProgress.errors > 0 ? 'var(--red)' : 'var(--text-muted)'};">{importProgress.errors}</div>
              <div class="stat-label">errors</div>
            </div>
          </div>
          {#if importProgress.errorRows.length}
            <details class="import-result-errors" open>
              <summary>Error details ({importProgress.errorRows.length}{importProgress.errors > importProgress.errorRows.length ? `, showing first ${importProgress.errorRows.length}` : ''})</summary>
              <div class="error-list">
                {#each importProgress.errorRows as e}
                  <div class="error-item">
                    <span class="error-row-num">Row {e.row}</span>
                    <span class="error-reason">{e.reason}</span>
                  </div>
                {/each}
              </div>
            </details>
          {/if}
          {#if ok}
            <div class="text-xs text-muted text-center mt-3">Closing...</div>
          {/if}
          <div class="row gap-lg mt-3" style="justify-content:center;">
            <button class="primary" onclick={closeImport}>Close</button>
          </div>
        </div>
      {:else if !importColumns.length}
        <div class="import-dropzone">
          <input
            id="csv-file"
            type="file"
            accept=".csv,text/csv"
            onchange={handleImportFile}
            style="display:none;"
          />
          <label for="csv-file" class="import-dropzone-inner">
            <div class="font-bold mb-1">Choose a CSV file</div>
            <div class="text-sm text-muted">Headers in the first row are auto-detected</div>
          </label>
        </div>
      {:else}
        <div class="row mb-2 gap-lg" style="flex-wrap:wrap; align-items:center;">
          <div class="text-sm">
            <span class="text-muted">File:</span>
            <span class="font-mono">{importFileName}</span>
            <span class="text-muted"> · {importRows.length} row{importRows.length === 1 ? '' : 's'} · {importColumns.length} column{importColumns.length === 1 ? '' : 's'}</span>
          </div>
          <label class="row" style="gap:6px; cursor:pointer;">
            <input type="checkbox" checked={importHasHeader} onchange={toggleHeader} disabled={importing} />
            <span class="text-sm">First row is a header</span>
          </label>
        </div>

        <div class="text-xs text-muted mb-1">Map each column. <b>Email</b> and <b>{$terms.counterparty_title}</b> are required.</div>
        <div class="mapping-table">
          <div class="mapping-row mapping-head">
            <div>Column</div>
            <div>Map to</div>
            <div>Sample</div>
          </div>
          {#each importColumns as col, i}
            <div class="mapping-row">
              <div class="mapping-col-name">{col}</div>
              <div>
                <select
                  class="mapping-select"
                  value={importMapping[i] ?? 'ignore'}
                  onchange={(e) => setMapping(i, (e.currentTarget as HTMLSelectElement).value as FieldKey)}
                  disabled={importing}
                >
                  {#each FIELD_OPTIONS as opt}
                    <option value={opt.value}>{opt.label}{opt.required ? ' *' : ''}</option>
                  {/each}
                </select>
              </div>
              <div class="mapping-sample">
                {#each importRows.slice(0, 2) as r}
                  <span class="mapping-sample-val">{r[i] || ''}</span>
                {/each}
              </div>
            </div>
          {/each}
        </div>

        {#if importPreviewRows.length}
          <div class="text-xs text-muted mt-3 mb-1">Preview (first {importPreviewRows.length} mapped rows):</div>
          <div class="preview-table">
            <div class="preview-row preview-head">
              <div>email</div><div>name</div><div>client</div><div>title</div><div>phone</div>
            </div>
            {#each importPreviewRows as p, idx}
              <div class="preview-row" class:invalid={!p}>
                {#if p}
                  <div>{p.email || ''}</div>
                  <div>{p.name || ''}</div>
                  <div>{p.client || ''}</div>
                  <div>{p.title || ''}</div>
                  <div>{p.phone || ''}</div>
                {:else}
                  <div style="grid-column: 1 / -1;" class="text-muted text-sm">Row {idx + 1}: missing email or client — will be skipped</div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}

        {#if !importHasTag && importColumns.length > 0}
          <div class="card mt-3" style="background: var(--bg-hover); padding: 0.75rem 1rem;">
            <div class="text-sm" style="margin-bottom: 0.4rem;">
              <strong>Tag this whole batch as:</strong>
              <span class="text-muted text-xs">
                — used by the Strafe filter to target a subset later. Leave blank to import as Untagged.
              </span>
            </div>
            <input
              type="text"
              bind:value={importDefaultTag}
              placeholder="e.g. Q2-outreach, Conf-leads"
              style="width: 280px;"
            />
          </div>
        {/if}

        {#if importing || importProgress.done > 0}
          <div class="import-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: {importProgress.total ? (importProgress.done / importProgress.total) * 100 : 0}%;"></div>
            </div>
            <div class="text-sm">
              {importProgress.done} / {importProgress.total} processed
              · {importProgress.imported} imported
              · {importProgress.skipped} skipped
              · {importProgress.errors} errors
            </div>
            {#if importProgress.errorRows.length}
              <details class="text-xs text-muted mt-2">
                <summary>Show {importProgress.errorRows.length} error{importProgress.errorRows.length === 1 ? '' : 's'}</summary>
                {#each importProgress.errorRows as e}
                  <div>Row {e.row}: {e.reason}</div>
                {/each}
              </details>
            {/if}
          </div>
        {/if}

        <div class="row gap-lg mt-3">
          <button
            class="primary"
            onclick={runImport}
            disabled={importing || !importHasEmail || !importHasClient || importRows.length === 0}
          >
            {importing ? 'Importing...' : `Import ${importRows.length} row${importRows.length === 1 ? '' : 's'}`}
          </button>
          <button onclick={closeImport} disabled={importing}>{importing ? 'Wait...' : 'Cancel'}</button>
          {#if !importHasEmail || !importHasClient}
            <span class="text-xs text-muted" style="align-self:center;">
              Map {!importHasEmail ? 'Email' : ''}{!importHasEmail && !importHasClient ? ' and ' : ''}{!importHasClient ? $terms.counterparty_title : ''} to enable import
            </span>
          {/if}
        </div>
      {/if}
    </div>
  </div>
{/if}

{#if addOpen}
  <div class="card mb-4">
    <div class="font-bold mb-3">Add Contact</div>
    <div class="grid-3 mb-2">
      <div>
        <label class="text-xs text-dim" for="new-email">Email *</label>
        <input id="new-email" bind:value={newEmail} placeholder="email@example.com" />
      </div>
      <div>
        <label class="text-xs text-dim" for="new-name">Name</label>
        <input id="new-name" bind:value={newName} placeholder="Full name" />
      </div>
      <div>
        <label class="text-xs text-dim" for="new-client">{$terms.counterparty_title} *</label>
        <input id="new-client" bind:value={newClient} placeholder={`${$terms.counterparty_title} name`} />
      </div>
    </div>
    <div class="grid-3 mb-3">
      <div>
        <label class="text-xs text-dim" for="new-title">Title</label>
        <input id="new-title" bind:value={newTitle} placeholder="Job title" />
      </div>
      <div>
        <label class="text-xs text-dim" for="new-phone">Phone</label>
        <input id="new-phone" bind:value={newPhone} placeholder="+61..." />
      </div>
      <div>
        <label class="text-xs text-dim" for="new-notes">Notes</label>
        <input id="new-notes" bind:value={newNotes} placeholder="Notes" />
      </div>
    </div>
    <div class="row">
      <button class="primary" onclick={handleAdd}>Save</button>
      <button onclick={() => addOpen = false}>Cancel</button>
    </div>
  </div>
{/if}

<!-- Promote dialog for new domain → client -->

<!-- Confirm Non-Clients dialog -->
{#if confirmNonClient}
  <div class="card mb-4" style="border: 2px solid var(--orange);">
    <div class="font-bold mb-2">Move "{confirmNonClient.replace(/_/g, ' ')}" to {$stageLabel('Non-Clients')}?</div>
    <div class="text-sm text-muted mb-3">
      This will stop it appearing in {$terms.counterparty} views. Future emails from its domains will go to {$stageLabel('Non-Clients')}. You can always change it back.
    </div>
    <div class="row gap-lg">
      <button style="background:var(--orange); color:white; border:none;" onclick={() => doStageChange(confirmNonClient, 'Non-Clients')}>Yes, move to {$stageLabel('Non-Clients')}</button>
      <button onclick={() => confirmNonClient = ''}>Cancel</button>
    </div>
  </div>
{/if}

{#if loading}
  <div class="card" style="text-align:center; padding:40px;">
    <span class="spinner spinner-lg"></span>
    <div class="text-muted mt-2">Loading contacts...</div>
  </div>
<!-- Three empty states, not one: "you have none", "your search matched none" and "your filter
     matched none" need different next actions, and a bare table header answers none of them.
     `.empty-state` is app.css's, the same one Dashboard and Prospecting use. -->
{:else if contacts.length === 0}
  <div class="empty-state">
    <div class="empty-state-icon">{search.trim() ? '🔍' : '👥'}</div>
    <div class="empty-state-text">
      {#if search.trim()}
        Nothing matched “{search.trim()}”. Contacts are searched by name, email, company and title.
      {:else}
        No contacts yet. They appear as {$agentName || 'George'} reads your email — or add one above.
      {/if}
    </div>
  </div>
{:else if shownCount === 0}
  <div class="empty-state">
    <div class="empty-state-icon">🗂️</div>
    <div class="empty-state-text">
      No contacts in {stageFilter.length === 1 ? stageFilter[0] : 'the selected stages'}.
      <button class="fchip-clear" onclick={clearFilters}>Clear the filter ✕</button>
    </div>
  </div>
{:else}
  {@const byClient = groupByClient(contacts)}
  {@const byStage = groupByStage(byClient)}
  <!-- Same wrapper Dashboard uses: a wide table scrolls inside this box instead of being
       clipped by `.main-content`'s `overflow-x: clip`. -->
  <div class="data-table-wrap">
  <table class="data-table contacts-table">
    <thead>
      <tr>
        <!-- Hints, not rules: this table uses AUTO layout (see `.contacts-table`), so the
             columns reflow to fill whatever width survives after the `@container` rules below
             drop one. `Contact` has no width and takes the remainder. -->
        <th style="width:32px;"></th>
        <th>Contact</th>
        <th class="col-email" style="width:280px;">Email</th>
        <th class="col-date" style="width:120px;">Last seen</th>
        <th class="num col-activity" style="width:90px;">Activity</th>
        <th style="width:140px;"></th>
      </tr>
    </thead>
    <tbody>
    {#each [...allStages, '— unset —'] as stageName}
      {#if byStage[stageName]?.length}
        {@const isStageOpen = expandedStages.has(stageName)}
        {@const ncCount = stageName === 'Non-Clients'
          ? byStage[stageName].reduce((sum, cn) => sum + (byClient[cn]?.length || 0), 0)
          : 0}
        <tr class="group-row"
          role="button" tabindex="0"
          onclick={() => toggleStageExpand(stageName)}
          onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleStageExpand(stageName)}
        >
          <td colspan="6">
            <div class="group-header">
              <span class="group-arrow">{isStageOpen ? '▼' : '▶'}</span>
              <span class="stage-dot" style="background:{stageColor(stageName)};"></span>
              <span class="group-name">{stageName}</span>
              {#if stageName === 'Non-Clients'}
                <span
                  class="nc-info"
                  onclick={(e) => { e.stopPropagation(); ncTipOpen = !ncTipOpen; }}
                >?</span>
                {#if ncTipOpen}
                  <span style="font-size:11px; color:var(--text-dim); margin-left:6px;">Only visible to you</span>
                {/if}
              {/if}
              <span class="group-meta">
                {#if stageName === 'Non-Clients'}
                  {ncCount} contact{ncCount !== 1 ? 's' : ''}
                {:else}
                  {byStage[stageName].length} client{byStage[stageName].length !== 1 ? 's' : ''}
                {/if}
                {#if !isStageOpen} · collapsed{/if}
              </span>
            </div>
          </td>
        </tr>

        {#if isStageOpen}
          {@const _limit = renderLimits[stageName] ?? INITIAL_CHUNK}
          {@const _visible = byStage[stageName].slice(0, _limit)}
          {#each _visible as cname}
            {#if cname === 'Non-Clients'}
              <!-- Non-Clients: split into orgGroups (by domain) + personalContacts (gmail/etc) -->
              {@const ncData = groupNonClientsByDomain(byClient['Non-Clients'] || [])}
              {#each Object.entries(ncData.orgGroups).sort((a, b) => a[0].localeCompare(b[0])) as [domain, domContacts]}
                {@const ncKey = `nc:${domain}`}
                {@const ncOpen = expandedClients.has(ncKey)}
                <tr class="subgroup-row"
                  role="button" tabindex="0"
                  onclick={() => toggleClientExpand(ncKey)}
                  onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleClientExpand(ncKey)}
                >
                  <td colspan="6">
                    <div class="subgroup-header">
                      <span class="subgroup-arrow">{ncOpen ? '▾' : '▸'}</span>
                      <span class="subgroup-name">{orgDisplayName(domain, domContacts)}</span>
                      <span class="subgroup-meta">{domContacts.length} contact{domContacts.length !== 1 ? 's' : ''}</span>
                      <button
                        class="promote-btn"
                        onclick={(e) => { e.stopPropagation(); openPromoteDialog(domain, domContacts); }}
                      >+ {$terms.counterparty_title}</button>
                    </div>
                  </td>
                </tr>
                {#if promoteDialog?.domain === domain}
                  <tr class="promote-row">
                    <td colspan="6">
                      <div class="promote-inline">
                        <input bind:value={promoteName} placeholder={`${$terms.counterparty_title} name`}
                          onkeydown={(e) => e.key === 'Enter' && handlePromote()} />
                        <button class="primary" onclick={handlePromote}>Create</button>
                        <button onclick={() => promoteDialog = null}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                {/if}
                {#if ncOpen}
                  {#each [...domContacts].sort((a, b) => b.last_seen.localeCompare(a.last_seen)) as contact}
                    {@render contactTr(contact)}
                  {/each}
                {/if}
              {/each}
              {#if ncData.personalContacts.length > 0}
                {#each Object.entries(
                  ncData.personalContacts.reduce((acc, c) => {
                    const key = c.name || c.email;
                    (acc[key] ??= []).push(c);
                    return acc;
                  }, {} as Record<string, Contact[]>)
                ).sort((a, b) => a[0].localeCompare(b[0])) as [personName, contacts]}
                  {@const pKey = 'nc:p:' + personName}
                  {@const pOpen = expandedClients.has(pKey)}
                  <tr class="subgroup-row"
                    role="button" tabindex="0"
                    onclick={() => toggleClientExpand(pKey)}
                    onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleClientExpand(pKey)}
                  >
                    <td colspan="6">
                      <div class="subgroup-header">
                        <span class="subgroup-arrow">{pOpen ? '▾' : '▸'}</span>
                        <span class="subgroup-name">{personName}</span>
                        <span class="subgroup-meta">{contacts[0].email}</span>
                        <button
                          class="promote-btn"
                          onclick={(e) => { e.stopPropagation(); openPromoteDialog(contacts[0].email, contacts); }}
                        >+ {$terms.counterparty_title}</button>
                      </div>
                    </td>
                  </tr>
                  {#if promoteDialog?.domain === contacts[0].email}
                    <tr class="promote-row">
                      <td colspan="6">
                        <div class="promote-inline">
                          <input bind:value={promoteName} placeholder={`${$terms.counterparty_title} name`}
                            onkeydown={(e) => e.key === 'Enter' && handlePromote()} />
                          <button class="primary" onclick={handlePromote}>Create</button>
                          <button onclick={() => promoteDialog = null}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  {/if}
                  {#if pOpen}
                    {#each contacts as contact}
                      {@render contactTr(contact)}
                    {/each}
                  {/if}
                {/each}
              {/if}
            {:else}
              <!-- Regular client sub-group with stage dropdown -->
              {@const grp = byClient[cname]}
              {@const latestDate = grp.reduce((best, c) => c.last_seen > best ? c.last_seen : best, '')}
              {@const days = daysAgo(latestDate)}
              {@const cOpen = expandedClients.has(cname)}
              <tr class="subgroup-row"
                role="button" tabindex="0"
                onclick={() => toggleClientExpand(cname)}
                onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleClientExpand(cname)}
              >
                <td colspan="6">
                  <div class="subgroup-header">
                    <span class="subgroup-arrow">{cOpen ? '▾' : '▸'}</span>
                    <span class="subgroup-name">{cname.replace(/_/g, ' ')}</span>
                    {#if days > 185 && days < 999}
                      <span class="badge badge-orange">Stale</span>
                    {/if}
                    <span class="subgroup-meta">
                      {grp.length} contact{grp.length !== 1 ? 's' : ''}
                      {#if days < 999} · {days}d ago{:else} · no activity{/if}
                    </span>
                    <button class="stage-pill" aria-haspopup="menu"
                      aria-expanded={stageMenu?.client === cname}
                      onclick={(e) => toggleStageMenu(e, cname)}>
                      <span class="stage-dot" style="background:{stageColor(stageOf(cname))};"></span>
                      {stageOf(cname)}
                      <span class="fchip-ar">▾</span>
                    </button>
                  </div>
                </td>
              </tr>
              {#if cOpen}
                {#each [...grp].sort((a, b) => b.last_seen.localeCompare(a.last_seen)) as contact}
                  {@render contactTr(contact)}
                {/each}
              {/if}
            {/if}
          {/each}
          {#if byStage[stageName].length > _limit}
            <tr class="load-more-row">
              <td colspan="6" style="padding:12px; text-align:center;">
                <button
                  onclick={() => loadMore(stageName)}
                  style="padding:6px 16px; font-size:12px; background:var(--bg-surface); border:1px solid var(--border); border-radius:4px; cursor:pointer;"
                >
                  Load {Math.min(CHUNK_INCREMENT, byStage[stageName].length - _limit)} more ({byStage[stageName].length - _limit} remaining)
                </button>
              </td>
            </tr>
          {/if}
        {/if}
      {/if}
    {/each}
    </tbody>
  </table>
  </div>
{/if}

{#if stageMenu}
  <div class="fchip-dd stage-menu" role="menu"
    style="position:fixed; top:{stageMenu.top}px; right:{stageMenu.right}px;">
    {#each allStages as s}
      {@const cur = stageOf(stageMenu.client)}
      <button class="fchip-opt" class:on={s === cur}
        role="menuitemradio" aria-checked={s === cur}
        onclick={() => { const c = stageMenu!.client; stageMenu = null; handleStageChange(c, s); }}>
        <span class="fchip-tick">✓</span>
        <span class="stage-dot" style="background:{stageColor(s)};"></span>{$stageLabel(s)}
      </button>
    {/each}
  </div>
{/if}

{#if toast}
  <div class="toast" class:toast-success={!toastError} class:toast-error={toastError}>{toast}</div>
{/if}

{#snippet contactTr(contact: Contact)}
  {#if editingEmail === contact.email}
    <tr class="contact-edit-row">
      <td colspan="6">
        <div class="contact-edit-grid">
          <div>
            <label class="text-xs text-dim" for="edit-name">Name</label>
            <input id="edit-name" bind:value={editName} />
          </div>
          <div>
            <label class="text-xs text-dim" for="edit-title">Title</label>
            <input id="edit-title" bind:value={editTitle} />
          </div>
          <div>
            <label class="text-xs text-dim" for="edit-phone">Phone</label>
            <input id="edit-phone" bind:value={editPhone} />
          </div>
          <div>
            <label class="text-xs text-dim" for="edit-linkedin">LinkedIn</label>
            <input id="edit-linkedin" bind:value={editLinkedin} placeholder="LinkedIn URL" />
          </div>
          <div style="grid-column: span 2;">
            <label class="text-xs text-dim" for="edit-notes">Notes</label>
            <input id="edit-notes" bind:value={editNotes} />
          </div>
        </div>
        <div class="row" style="margin-top:10px; gap:8px;">
          <button class="primary" onclick={() => handleSaveEdit(contact)}>Save</button>
          <button onclick={() => editingEmail = ''}>Cancel</button>
        </div>
      </td>
    </tr>
  {:else}
    {@const ago = daysAgo(contact.last_seen)}
    <!-- The client to open comes from the CONTACT, not from the group heading it is sitting
         under: under `Non-Clients` or `— Unknown —` that heading is a bucket label, not a
         client, and navigating to it asks for `/clients/Non-Clients`. A contact with no client
         has nothing to open, so the button is not offered. -->
    {@const openTarget = (contact.client || '').trim()}
    <tr class="data-row contact-tr">
      <td></td>
      <td class="cell-contact">
        <div class="contact-cell">
          <div class="contact-avatar" style="background:{avatarColor(contact.email)};">{initialsFor(contact.name, contact.email)}</div>
          <div class="contact-meta">
            <div class="contact-name">{contact.name || '(unnamed)'}</div>
            {#if contact.title}
              <div class="contact-sub">{contact.title}{#if contact.phone} · {contact.phone}{/if}</div>
            {:else if contact.phone}
              <div class="contact-sub">{contact.phone}</div>
            {/if}
          </div>
        </div>
      </td>
      <td class="cell-email col-email"><span class="email-mono">{contact.email}</span></td>
      <td class="cell-date col-date {ago > 30 && ago < 999 ? 'date-warm' : ''}">{contact.last_seen || '—'}</td>
      <td class="num col-activity">{ago < 999 ? `${ago}d ago` : '—'}</td>
      <td class="cell-actions">
        <div class="row-actions">
          {#if openTarget}
            <button class="row-mini-btn" title="Open {contact.name || contact.email} in {openTarget.replace(/_/g, ' ')}"
              onclick={(e) => {
                e.stopPropagation();
                const cl = normalizeClientName(openTarget);
                // Seed BEFORE navigating: ClientView reads it as it mounts.
                $clientContactSeed = { client: cl, email: contact.email };
                $currentView = cl;
              }}>Open</button>
          {/if}
          <button class="row-mini-btn" title="Edit contact" onclick={(e) => { e.stopPropagation(); startEdit(contact); }}>Edit</button>
          <button class="row-mini-btn danger" title="Delete contact" onclick={(e) => { e.stopPropagation(); handleDelete(contact); }}>×</button>
        </div>
      </td>
    </tr>
    {#if contact.notes}
      <tr class="contact-notes-row">
        <td colspan="6"><span class="text-xs text-dim"><svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg> {contact.notes}</span></td>
      </tr>
    {/if}
  {/if}
{/snippet}

<style>
  /* Contacts page header actions — sits between the toolbar and the
     table. Count on the left, action chip-buttons on the right. */
  /* Matches `.fchip`'s geometry — this button shares a row with them, and a 5px-radius
     rectangle beside a 20px pill reads as two unrelated toolbars. */
  .action-btn {
    display: inline-flex;
    align-items: center;
    background: transparent;
    color: var(--text-muted);
    border: 1px solid var(--border);
    padding: 7px 13px;
    border-radius: 20px;
    font-size: 12px;
    /* Export is an `<a>`, its three neighbours are `<button>`s, and the global `button` rule
       sets 500 — without this the one link in the row renders a weight lighter than the
       buttons it is lined up with. */
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    text-decoration: none;
    white-space: nowrap;
    transition: all 0.1s;
  }
  .action-btn:hover {
    color: var(--text);
    border-color: var(--border-strong);
    background: var(--bg-hover);
  }
  .action-btn.primary-action {
    background: var(--accent);
    color: #fff;
    border-color: var(--accent);
  }
  .action-btn.primary-action:hover {
    background: color-mix(in srgb, var(--accent) 88%, black);
    border-color: color-mix(in srgb, var(--accent) 88%, black);
  }

  /* Without this the nowrap contact name and the action buttons — neither can shrink —
     push the table past `.main-content`, which is `overflow-x: clip`: the overflow is cut
     off, not scrollable. Scoped here; the Dashboard's `.data-table` must stay auto-layout.
     See UPDATES 2026-08-17 (jj). */
  /* 🔴 NO `table-layout: fixed` here, deliberately — and do not add it back.
     With it, the one column that has no width (`Contact`) is given its HEADER'S CONTENT
     width instead of the table's remaining space, so the columns stop summing to the table.
     Measured: element 480px, columns summing to 354 — 32 + 120 + 140 of fixed columns plus
     62px, which is exactly the rendered width of the word "CONTACT". The 126px difference
     renders as trailing white space while the names truncate. Neither `min-width` nor
     `width: max(100%, …)` fixes it; both change the element's width without changing how the
     columns are distributed.
     Auto layout treats the px widths below as hints and lets the surviving columns reflow to
     fill the container — which is the whole adaptation strategy (see `.data-table-wrap` in
     `app.css`). Dashboard has always used auto layout and has never had this bug. */

  /* Fixed layout clips instead of pushing, so every cell must say how it degrades. */
  .contacts-table .cell-email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .contacts-table .cell-date,
  .contacts-table .col-activity { white-space: nowrap; }

  /* Sub-group rows — sit between the heavy stage group rows and the
     lightweight data rows. Lighter background, no full-width tint, just a
     subtle left-indent and a divider underneath. Reads as a section
     heading within the stage rather than a competing row. */
  .contacts-table tbody tr.subgroup-row {
    background: transparent;
    cursor: pointer;
    border-top: 1px solid var(--border);
  }
  .contacts-table tbody tr.subgroup-row:hover { background: var(--bg-hover); }
  /* Padding is a floor only — `--row-h` (app.css) sets the height, same as every other row
     in every table. Keep it under `(--row-h - 22px) / 2` so the stage pill never lifts the
     row above it. */
  .contacts-table tbody tr.subgroup-row td {
    padding: 4px 14px 4px 32px;
    border-bottom: none;
  }
  .subgroup-header {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12.5px;
    min-width: 0;
  }
  .subgroup-arrow {
    color: var(--text-dim);
    font-size: 9px;
    width: 10px;
    display: inline-flex;
    justify-content: center;
  }
  /* The name is the only part allowed to give ground — the stage pill is pinned right and
     does not shrink, so without this a long client name pushes it off the table's right
     edge, where `.main-content`'s `overflow-x: clip` eats it. */
  .subgroup-name {
    color: var(--text);
    font-weight: 600;
    letter-spacing: -0.005em;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .subgroup-meta {
    color: var(--text-dim);
    font-size: 11px;
    font-family: var(--font-mono);
    margin-right: auto;
    white-space: nowrap;
    /* Must be able to shrink: this sits in a `colspan` row, so if it refuses to narrow it
       sets the whole table's width and every data row gets trailing space beside it. */
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* Both inherited from the `<select>` this replaced: it must not shrink (the client name
     gives ground instead) and must not wrap — a two-line pill lifts the row past `--row-h`. */
  .subgroup-header .stage-pill {
    flex-shrink: 0;
    white-space: nowrap;
  }
  .promote-btn {
    font-size: 10.5px;
    padding: 3px 9px;
    background: transparent;
    border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
    border-radius: 4px;
    color: var(--accent-ink);
    cursor: pointer;
    font-weight: 500;
  }
  .promote-btn:hover {
    background: color-mix(in srgb, var(--accent) 12%, transparent);
    border-color: var(--accent);
  }
  /* 5px, not 8: the inline input is ~27px, and 8px either side put this row at 43px — over
     `--row-h`, so it was the one row in the table that did not line up. */
  .promote-row td { padding: 5px 14px 5px 32px; background: var(--bg); }
  .promote-inline {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .promote-inline input {
    flex: 1;
    font-size: 12px;
    padding: 5px 10px;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 5px;
    color: var(--text);
  }
  .promote-inline button { font-size: 11px; padding: 5px 12px; }

  /* Contact rows — the actual data rows. Avatar + name+sub gives each row
     visual weight without becoming a card. */
  /* `--row-h` sets the height; this padding only has to leave room for the tallest content so
     the row never exceeds it. Two lines of contact text at 1.2 = ~29px, the avatar 26px — both
     clear 40px with 4px either side. Raise this and two-line rows grow taller than one-line ones. */
  /* `.data-row` (app.css) hands out `cursor: pointer` because on Dashboard and Prospecting a
     row click expands it. A contact row has no row-level action — the actions are the buttons
     — so it must not advertise one. Hover stays; the pointer goes. */
  .contacts-table tbody tr.contact-tr { cursor: default; }
  .contacts-table tbody tr.contact-tr td {
    padding: 4px 14px;
    border-bottom: 1px solid var(--border-soft, var(--border));
  }
  .cell-contact { padding-left: 28px !important; }
  .contact-cell {
    display: flex;
    align-items: center;
    gap: 11px;
    min-width: 0;
  }
  .contact-avatar {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 10.5px;
    font-weight: 600;
    flex-shrink: 0;
    letter-spacing: 0.02em;
  }
  .contact-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 0;
  }
  .contact-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .contact-sub {
    font-size: 11.5px;
    color: var(--text-dim);
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .email-mono {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-muted);
  }
  /* 🔴 The flex box goes on the inner div, NEVER on the `<td>`. A `display:` on a cell
     takes it out of table layout — it stops sharing the row's top edge and stops being
     bound by its column. Same rule as `.conv-subject-cell` (Prospecting). */
  .cell-actions { text-align: right; }
  .row-actions {
    display: flex;
    gap: 4px;
    justify-content: flex-end;
  }
  .row-mini-btn {
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-muted);
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    font-family: inherit;
  }
  .row-mini-btn:hover { color: var(--text); border-color: var(--border-strong); }
  .row-mini-btn.danger { color: var(--red); }
  .row-mini-btn.danger:hover { background: color-mix(in srgb, var(--red) 12%, transparent); border-color: var(--red); }

  .contacts-table tbody tr.contact-notes-row td {
    padding: 2px 14px 7px;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
  }

  /* Inline edit row — flips a contact's <tr> into an editor */
  .contacts-table tbody tr.contact-edit-row td {
    padding: 14px;
    background: var(--bg);
  }
  .contact-edit-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  .contact-edit-grid input { width: 100%; font-size: 12px; padding: 5px 8px; }

  /* Non-Clients tooltip ? */
  .nc-info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px; height: 14px;
    border-radius: 50%;
    border: 1px solid var(--border);
    font-size: 9px;
    color: var(--text-muted);
    cursor: pointer;
    font-weight: 600;
  }

  /* Responsive: drop optional columns at narrow widths. `display: none` is the one
     `display` a cell may carry — the selector hits that column's `<th>` AND its `<td>`s,
     so the whole column leaves together. Any other value breaks a single cell out of the
     row (see `.cell-actions`). */
  /* Progressive column removal, in PRIORITY order, keyed on the TABLE'S CONTAINER — not the
     viewport, which is wrong by construction for any table inside `.split-main`.
     Order, and why:
       1. Activity  — "4d ago" is `Last seen` in another format. Dropping it costs nothing.
       2. Email     — recoverable: the row opens the contact.
       3. Last seen — the scanning signal, so it goes last.
     What always survives: avatar · name · the row actions. Each rule hides the `<th>` AND its
     `<td>`s through the shared `col-*` class, so the whole column leaves together.
     🔴 `display: none` is the ONLY `display` a table cell may carry — any other value breaks
     that cell out of the row (see `.cell-actions`). */
  @container data-table (max-width: 800px) {
    .contacts-table .col-activity { display: none; }
  }
  @container data-table (max-width: 720px) {
    .contacts-table .col-email { display: none; }
  }
  @container data-table (max-width: 480px) {
    .contacts-table .col-date { display: none; }
  }

  /* ─── CSV import modal ─── */
  .import-overlay {
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex; align-items: flex-start; justify-content: center;
    padding: 40px 16px; overflow-y: auto;
    z-index: 1000;
  }
  .import-modal {
    width: 100%; max-width: 720px;
    background: var(--bg-surface);
    padding: 18px 20px;
  }
  .import-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 12px;
  }
  .import-close {
    background: transparent; border: none; color: var(--text-muted);
    font-size: 22px; line-height: 1; padding: 0 6px; cursor: pointer;
  }
  .import-close:hover { color: var(--text); background: var(--bg-hover); }
  .import-dropzone {
    border: 2px dashed var(--border-strong);
    border-radius: var(--radius);
    padding: 32px;
    transition: border-color 0.15s, background 0.15s;
  }
  .import-dropzone:hover { border-color: var(--accent); }
  .import-dropzone-inner {
    display: flex; flex-direction: column; align-items: center;
    cursor: pointer;
  }
  .mapping-table {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    overflow: hidden;
  }
  .mapping-row {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1.6fr);
    gap: 12px;
    padding: 8px 12px;
    align-items: center;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
  }
  .mapping-row:last-child { border-bottom: none; }
  .mapping-head {
    background: var(--bg-hover);
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
  }
  .mapping-col-name {
    font-family: var(--font-mono);
    font-size: 12px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .mapping-select {
    width: 100%;
    /* Right padding clears the app-wide select arrow (app.css). */
    padding: 4px 22px 4px 8px;
    font-size: 12px;
    border: 1px solid var(--border);
    border-radius: 4px;
    background-color: var(--bg);   /* NOT `background:` — wipes the select arrow */
    color: var(--text);
  }
  .mapping-sample {
    display: flex; flex-direction: column; gap: 2px;
    overflow: hidden;
    font-size: 11px;
    color: var(--text-muted);
  }
  .mapping-sample-val {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .preview-table {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    overflow: hidden;
  }
  .preview-row {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 0.8fr);
    gap: 10px;
    padding: 6px 12px;
    font-size: 12px;
    border-bottom: 1px solid var(--border);
  }
  .preview-row:last-child { border-bottom: none; }
  .preview-row > div {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .preview-head {
    background: var(--bg-hover);
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
  }
  .preview-row.invalid { background: color-mix(in srgb, var(--orange, #f97316) 6%, transparent); }
  .import-progress { margin-top: 14px; }
  .progress-bar {
    height: 6px;
    background: var(--bg-hover);
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 6px;
  }
  .progress-fill {
    height: 100%;
    background: var(--accent);
    transition: width 0.2s ease;
  }
  .import-result {
    text-align: center;
    padding: 8px 4px;
  }
  .import-result-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    font-weight: 700;
    line-height: 1;
    margin: 4px auto 12px;
    color: white;
  }
  .import-result-icon.ok   { background: var(--green); }
  .import-result-icon.warn { background: var(--orange); }
  .import-result-icon.err  { background: var(--red); }
  .import-result-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
  }
  .import-result-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    max-width: 360px;
    margin: 0 auto 8px;
  }
  .import-result-stats .stat {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 10px 6px;
    background: var(--bg);
  }
  .import-result-stats .stat-num {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.1;
  }
  .import-result-stats .stat-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .import-result-errors {
    margin-top: 16px;
    text-align: left;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 8px 12px;
    background: var(--bg);
  }
  .import-result-errors summary {
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: var(--red);
    user-select: none;
  }
  .error-list {
    margin-top: 8px;
    max-height: 160px;
    overflow-y: auto;
  }
  .error-item {
    display: grid;
    grid-template-columns: 70px 1fr;
    gap: 8px;
    padding: 4px 0;
    border-top: 1px solid var(--border);
    font-size: 12px;
  }
  .error-item:first-child { border-top: none; }
  .error-row-num {
    color: var(--text-muted);
    font-family: var(--font-mono);
  }
  .error-reason {
    color: var(--text);
    word-break: break-word;
  }
  .text-center { text-align: center; }
</style>
